// Typed client for /api/v1/workspaces/{slug}/folders: the user-made folders inside a workspace's
// own content ("Workspace content" in the Scene Manager). Persisted server-side
// (platform_db.workspace_folders); measurements are filed into them via their folder_id.

import { apiFetch } from './platformApi';

export interface ApiFolder {
  folder_id: string;
  name: string;
  sort_order: number;
  created_by: string;
  created_at: string;
}

const base = (workspace: string) => `/api/v1/workspaces/${encodeURIComponent(workspace)}/folders`;

/** Error carrying the HTTP status, so callers can tell "name already taken" (409) apart. */
export class FolderApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function fetchFolders(workspace: string): Promise<ApiFolder[]> {
  const res = await apiFetch(base(workspace));
  if (!res.ok) throw new FolderApiError(`GET folders -> ${res.status}`, res.status);
  const body: unknown = await res.json();
  if (!Array.isArray(body)) throw new Error('GET folders: expected a JSON array');
  return body as ApiFolder[];
}

export async function createFolder(workspace: string, name: string): Promise<ApiFolder> {
  const res = await apiFetch(base(workspace), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new FolderApiError(`POST folders -> ${res.status}`, res.status);
  return (await res.json()) as ApiFolder;
}

export async function renameFolder(workspace: string, folderId: string, name: string): Promise<ApiFolder> {
  const res = await apiFetch(`${base(workspace)}/${encodeURIComponent(folderId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new FolderApiError(`PATCH folder -> ${res.status}`, res.status);
  return (await res.json()) as ApiFolder;
}

export async function deleteFolder(workspace: string, folderId: string): Promise<void> {
  const res = await apiFetch(`${base(workspace)}/${encodeURIComponent(folderId)}`, { method: 'DELETE' });
  if (!res.ok) throw new FolderApiError(`DELETE folder -> ${res.status}`, res.status);
}
